// btw-front/src/components/deal/DealMessagesSection.jsx
// 딜 관리자 메시지 섹션 — 딜 상세(파트너/관리자 공용). 관리자 → 파트너 공개 스레드(1차 단방향).
// 작성 권한(canWrite)은 부모가 판정: 관리자 + 파트너 소유 딜 + 삭제되지 않은 딜.
// 알림 메일 상태(mailStatus)는 관리자 응답에만 있어 관리자 화면에서만 뱃지로 표시.
// 관리자 내부 메모(비공개, 앰버 톤)와 혼동하지 않도록 힌트에 "파트너에게 공개" 를 명시.
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { message } from "antd";
import { Badge } from "../ui/Badge";
import { Button } from "../ui/Button";
import { Textarea } from "../ui/Textarea";
import {
  listMessages as listMessagesAdmin,
  createMessage as createMessageAdmin,
} from "../../api/adminDeals";
import { listMessages as listMessagesPartner } from "../../api/partnerDeals";

// 서버 검증(DEAL_MESSAGE_MAX_LENGTH)과 동일
const MAX_LENGTH = 2000;

// 서버 ENUM(mail_status) → Badge 톤
const MAIL_TONE = {
  sent: "approved",
  failed: "rejected",
  no_recipient: "neutral",
  pending: "pending",
};

// 서버 DATETIME(ISO 문자열) → 브라우저 로컬 기준 "yyyy-MM-dd HH:mm"
function formatDateTime(value) {
  if (!value) return "";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return String(value);
  const p = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())} ${p(d.getHours())}:${p(d.getMinutes())}`;
}

/**
 * props
 *  - dealId: string
 *  - isAdmin: boolean — 목록 API 선택 + 메일 상태 뱃지 노출
 *  - canWrite: boolean — 작성 폼 노출
 *  - recipientEmail: string|null — 현재 딜의 영업대표 이메일(작성 폼 하단 안내용)
 */
export default function DealMessagesSection({ dealId, isAdmin, canWrite, recipientEmail }) {
  const { t } = useTranslation();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [content, setContent] = useState("");
  const [sending, setSending] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    const fetcher = isAdmin ? listMessagesAdmin : listMessagesPartner;
    fetcher(dealId)
      .then((rows) => {
        if (!cancelled) setItems(rows);
      })
      .catch(() => {
        if (!cancelled) setItems([]);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [dealId, isAdmin]);

  async function handleSend() {
    if (sending) return;
    const body = content.trim();
    if (!body) {
      message.warning(t("partners.deals.detail.messages.required"));
      return;
    }
    setSending(true);
    try {
      const item = await createMessageAdmin(dealId, body);
      // 서버가 최종 상태(메일 결과 반영) 행을 돌려주므로 재조회 없이 목록 맨 앞에 추가
      setItems((prev) => [item, ...prev]);
      setContent("");
      const status = item && item.mailStatus;
      if (status === "sent") message.success(t("partners.deals.detail.messages.sentOk"));
      else if (status === "no_recipient")
        message.warning(t("partners.deals.detail.messages.savedNoRecipient"));
      else if (status === "failed")
        message.warning(t("partners.deals.detail.messages.savedMailFailed"));
      else message.warning(t("partners.deals.detail.messages.savedMailUnknown"));
    } catch (err) {
      const msg =
        (err && err.payload && err.payload.error && err.payload.error.message) ||
        t("partners.deals.detail.messages.sendFailed");
      message.error(msg);
    } finally {
      setSending(false);
    }
  }

  return (
    <section className="rounded-2xl border border-slate-200 bg-white p-5 md:p-7">
      <div className="px-1">
        <h2 className="text-xs font-semibold uppercase tracking-wider text-slate-500">
          {t(
            isAdmin
              ? "partners.deals.detail.messages.titleAdmin"
              : "partners.deals.detail.messages.titlePartner",
          )}
        </h2>
        <p className="mt-1 text-[11px] text-slate-500">
          {t(
            isAdmin
              ? "partners.deals.detail.messages.hintAdmin"
              : "partners.deals.detail.messages.hintPartner",
          )}
        </p>
      </div>

      {/* 관리자 작성 폼 — 목록 위. 수신자 유무를 폼 하단에 미리 안내(작성 후 놀라지 않도록) */}
      {canWrite && (
        <div className="mt-3">
          <Textarea
            rows={4}
            maxLength={MAX_LENGTH}
            value={content}
            placeholder={t("partners.deals.detail.messages.placeholder")}
            onChange={(e) => setContent(e.target.value)}
          />
          <div className="mt-2 flex flex-wrap items-center justify-between gap-2">
            <span className={`text-xs ${recipientEmail ? "text-slate-500" : "text-amber-700"}`}>
              {recipientEmail
                ? t("partners.deals.detail.messages.recipient", { email: recipientEmail })
                : t("partners.deals.detail.messages.noRecipientHint")}
            </span>
            <Button
              variant="accent"
              size="sm"
              onClick={handleSend}
              disabled={!content.trim()}
              loading={sending}
            >
              {sending
                ? t("partners.deals.detail.messages.sending")
                : t("partners.deals.detail.messages.send")}
            </Button>
          </div>
        </div>
      )}

      <div className={canWrite ? "mt-5" : "mt-3"}>
        {loading ? (
          <p className="text-sm text-slate-400">{t("common.loading")}</p>
        ) : !items.length ? (
          <p className="text-sm text-slate-500">{t("partners.deals.detail.messages.empty")}</p>
        ) : (
          <ol className="space-y-0">
            {items.map((m, idx) => {
              const last = idx === items.length - 1;
              return (
                <li key={m.id} className="flex gap-3">
                  {/* 타임라인 레일 — 도트 + 다음 항목으로 잇는 세로선 (변경 이력과 동일 구조) */}
                  <div className="flex flex-col items-center" aria-hidden="true">
                    <span className="mt-1 h-2.5 w-2.5 shrink-0 rounded-full bg-accent ring-4 ring-accent/10" />
                    {!last && <span className="w-px flex-1 bg-slate-200" />}
                  </div>
                  <div className={`min-w-0 flex-1 ${last ? "" : "pb-5"}`}>
                    <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
                      <time
                        dateTime={m.createdAt ? String(m.createdAt) : undefined}
                        className="text-xs font-medium tabular-nums text-slate-700"
                      >
                        {formatDateTime(m.createdAt)}
                      </time>
                      <span className="text-slate-300" aria-hidden="true">
                        ·
                      </span>
                      <span className="text-xs text-slate-600">
                        {m.createdByAdminName || t("partners.deals.detail.messages.unknownAuthor")}
                      </span>
                      <span className="rounded bg-accent/10 px-1.5 py-0.5 text-[10px] font-medium text-accent">
                        {t("partners.deals.detail.messages.adminActor")}
                      </span>
                      {/* 메일 상태 — 관리자 전용. 행의 수신자(작성 시점 스냅샷)는 title 로 노출 */}
                      {isAdmin && m.mailStatus && (
                        <span className="ml-auto" title={m.recipientEmail || undefined}>
                          <Badge tone={MAIL_TONE[m.mailStatus] || "neutral"}>
                            {t(`partners.deals.detail.messages.mailStatus.${m.mailStatus}`)}
                          </Badge>
                        </span>
                      )}
                    </div>
                    <p className="mt-2 whitespace-pre-wrap text-sm leading-relaxed text-slate-800">
                      {m.content}
                    </p>
                  </div>
                </li>
              );
            })}
          </ol>
        )}
      </div>
    </section>
  );
}
